// Injects the desktop dictation bridge (`src/tauri/dictation/**`) into
// @heidi/dictate's bridge-port seam (ruling 25). Imported for
// effect in `_app.tsx`, same as the hotkey/keyboard/permissions wirings.
//
// THE ANNOTATION IS THE PROOF. `DesktopDictationPort` is declared
// structurally in `@heidi/dictate/contracts` because that door may not name a
// Tauri type or an app-side union, so nothing else checks that the real
// bridge functions fit it. The `satisfies` annotation below is that check —
// deliberately not a cast.
//
// EVERY MEMBER IS A REFERENCE, NEVER A WRAPPER. Each bridge function
// short-circuits on `!isTauri()` before it reaches `invoke`/`listen`
// (`bootstrapAnonymousAccessToken` / `refreshAnonymousAccessToken` are the
// documented exceptions — desktop-only, they throw off-Tauri by design), so
// forwarding the function forwards the guard. `dictation-bridge-wiring.test.ts`
// pins every reference by identity for exactly that reason.
//
// `setDictationHotkey` and the permission calls are NOT here: they cross
// through their own seams (`dictation-hotkey-wiring`,
// `dictation-permissions-wiring`) and one port type per bridge surface is
// ruling 25's own falsifier.

import {
  bootstrapAnonymousAccessToken,
  clearAnonymousAccess,
  loadAnonymousAccess,
  refreshAnonymousAccessToken,
  saveAnonymousAccess,
} from '@/tauri/dictation/anonymousAccess';
import {
  cancelNativeDictation,
  clearDictateOnboardingData,
  emitDictationResultSuppressed,
  listenChordPreview,
  listenDictationAnonymousLimitReached,
  listenDictationProcessing,
  listenDictationResult,
  listenDictationResultSuppression,
  listenDictationStart,
  listenHandsfreeStart,
  loadDictateOnboardingData,
  playDictationSound,
  saveDictateOnboardingData,
  setChordPreviewEnabled,
  setDictationInputDevice,
  setOnboardingDictationEnabled,
  startProgrammaticDictation,
  stopNativeDictation,
} from '@/tauri/dictation/api';
import {
  updateAnonymousOnboardingDictationConfig,
  updateAnonymousTrialDictationConfig,
  updateNativeDictationConfig,
} from '@/tauri/dictation/config';
import { shouldUseNativeAudio } from '@/tauri/dictation/dictation-provider.utils';
import { configureDictationBridgePort } from '@heidi/dictate';
import type { DesktopDictationPort } from '@heidi/dictate/contracts';

/**
 * Declared at module scope and exported for its identity test — the same
 * discipline as `tauri-widget-port.ts`.
 */
export const tauriDictationBridge = {
  setOnboardingDictationEnabled,
  setDictationInputDevice,
  cancelNativeDictation,
  stopNativeDictation,
  startProgrammaticDictation,
  emitDictationResultSuppressed,
  playDictationSound,
  shouldUseNativeAudio,
  setChordPreviewEnabled,
  listenDictationStart,
  listenDictationProcessing,
  listenDictationResult,
  listenHandsfreeStart,
  listenDictationAnonymousLimitReached,
  listenDictationResultSuppression,
  listenChordPreview,
  updateNativeDictationConfig,
  updateAnonymousOnboardingDictationConfig,
  updateAnonymousTrialDictationConfig,
  saveAnonymousAccess,
  loadAnonymousAccess,
  clearAnonymousAccess,
  bootstrapAnonymousAccessToken,
  refreshAnonymousAccessToken,
  saveDictateOnboardingData,
  loadDictateOnboardingData,
  clearDictateOnboardingData,
} satisfies DesktopDictationPort;

configureDictationBridgePort(tauriDictationBridge);
